/**
 * Conversaciones (`/activa/oficial/conversaciones`): la lista y, al lado, la conversación abierta (§15.3).
 *
 * - Lado a lado recién desde `xl` (1280 px). `lg:` mide el viewport (1024 px), pero con el menú lateral de
 *   220 px la lista quedaba en unos 290 px. Hasta `xl` (y en el celular) van en páginas separadas: con una
 *   conversación abierta se oculta la lista, y sin ninguna se oculta el hilo.
 * - Desde `xl` la grilla ocupa el alto que le deja `OficialLayout` (`flex-1`) en UNA fila de ese alto: las dos
 *   columnas miden lo mismo, la lista con su scroll y la del hilo en columna (ficha, hilo y caja, en
 *   `ContenidoConversacion`). Nada de `sticky` ni de altos calculados contra `100vh`: no saben cuánto mide lo de arriba.
 * - Los filtros viven en la URL (`?filtro=&campana=&q=&page=`): un enlace o un F5 muestran lo mismo.
 * - Una respuesta vieja (se tipeó rápido o se cambiaron dos filtros seguidos) no pisa a la nueva.
 * - Se refresca cada 20 s y, además, cuando alguien avisa un cambio: después de contestar, la fila no
 *   puede seguir diciendo «Sin responder» 20 s mientras el hilo ya dice «Respondida».
 * - El título y los filtros van arriba, a lo ancho de las dos columnas (auditoría de diseño del 18/10/2026): el título
 *   estaba adentro de la tarjeta de la lista, debajo de los filtros, y en la columna de la lista los chips se partían en
 *   dos renglones. Con una conversación abierta, hasta `xl` se ocultan con la lista: son de la lista.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useMatch, useSearchParams } from "react-router-dom";
import { conversacionesApi } from "../../conversacionesApi";
import type { FiltroConversaciones, PaginaConversaciones } from "../../types.conversaciones";
import { usePolling } from "../../hooks/usePolling";
import { llamar, textoFallo } from "../../components/base/comun";
import { FiltrosConversaciones } from "../../components/conversaciones/FiltrosConversaciones";
import { ListaConversaciones } from "../../components/conversaciones/ListaConversaciones";
import { TarjetaResolverVencidas } from "../../components/conversaciones/TarjetaResolverVencidas";
import { EVENTO_CONVERSACIONES_CAMBIO, desfaseMs, filtroDeUrl } from "../../logic/conversaciones";
import { num } from "../../../campana-nueva/utils/format";

const LIMITE = 50;
const REFRESCO_MS = 20_000;

export default function ConversacionesLayout() {
  const match = useMatch("/activa/oficial/conversaciones/:id");
  const hayId = !!match;
  const seleccionadaId = match?.params.id ?? null;

  const [sp, setSp] = useSearchParams();
  const { filtro, campana, q, page } = filtroDeUrl(sp);

  const [pagina, setPagina] = useState<PaginaConversaciones | null>(null);
  // Con qué filtros se trajo `pagina`: si falla la carga de otros, sus filas no pueden quedar como si fueran de éstos.
  const filtros = [filtro, campana, q, page].join("|");
  const [paginaDe, setPaginaDe] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [desfase, setDesfase] = useState(0);
  const [texto, setTexto] = useState(q);
  const [resolviendo, setResolviendo] = useState(false);
  const seq = useRef(0);
  const vivo = useRef(true);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  /** Cambia un filtro de la URL y vuelve a la primera página. Nunca toca `accion` ni el `:id`. */
  const setFiltroUrl = useCallback((k: "filtro" | "campana" | "q", v: string) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (v) n.set(k, v); else n.delete(k);
      n.delete("page");
      return n;
    }, { replace: true });
  }, [setSp]);

  // El texto del buscador sigue a la URL (un link con `?q=`, volver atrás), salvo cuando `q` es lo que ya dice el buscador:
  // el debounce escribe el texto recortado, y pisarlo le sacaba al operador el espacio del final mientras escribía
  // («maria » quedaba «maria» y se buscaba «marialopez»).
  useEffect(() => { setTexto((t) => (t.trim() === q ? t : q)); }, [q]);

  // Debounce de 400 ms del buscador (como Contactos).
  useEffect(() => {
    if (texto.trim() === q) return;
    const t = setTimeout(() => setFiltroUrl("q", texto.trim().slice(0, 100)), 400);
    return () => clearTimeout(t);
  }, [texto, q, setFiltroUrl]);

  /** Pide la página. Devuelve false si falló (el polling lo cuenta como error). */
  const cargar = useCallback(async (): Promise<boolean> => {
    const mio = ++seq.current;
    const l = await llamar<PaginaConversaciones>(() => conversacionesApi.listar({
      filtro, campana: campana || undefined, q: q || undefined, page, limit: LIMITE,
    }));
    if (!vivo.current || mio !== seq.current) return true;
    setCargando(false);
    if (!l.ok || !l.data) {
      setError(l.ok ? "El servidor no devolvió las conversaciones." : textoFallo(l));
      return false;
    }
    setPagina(l.data);
    setPaginaDe(filtros);
    setDesfase(desfaseMs(l.data.ahoraServidor, Date.now()));
    setError("");
    return true;
  }, [filtro, campana, q, page, filtros]);

  useEffect(() => {
    setCargando(true);
    void cargar();
  }, [cargar]);

  useEffect(() => {
    const alCambiar = () => { void cargar(); };
    window.addEventListener(EVENTO_CONVERSACIONES_CAMBIO, alCambiar);
    return () => window.removeEventListener(EVENTO_CONVERSACIONES_CAMBIO, alCambiar);
  }, [cargar]);

  const poll = usePolling(async () => {
    if (!(await cargar())) throw new Error("no se pudo refrescar la lista");
  }, REFRESCO_MS, [filtro, campana, q, page]);

  // Falló la carga de otro chip o de otra búsqueda: las filas de antes seguían a opacidad normal y sin aviso, y el chip
  // decía «Resueltas» sobre las «Por contestar». Sin ellas la lista dice que no pudo, con Reintentar. Un refresco que
  // falla con los MISMOS filtros las deja: para eso está el aviso del polling detenido.
  const filasDeOtrosFiltros = !!pagina && !!error && !cargando && paginaDe !== filtros;
  const vencidas = pagina ? Math.max(0, (pagina.contador?.sinResponder ?? 0) - (pagina.contador?.porContestar ?? 0)) : 0;
  // La tarjeta queda abierta después de resolver (dice «Listo…») aunque ya no queden vencidas.
  const mostrarBotonResolver = filtro === "sin_responder" && vencidas > 0 && !resolviendo;

  return (
    <div className="flex flex-col gap-4 xl:min-h-0 xl:flex-1">
      <div data-testid="cabeza-conversaciones" className={`space-y-4 ${hayId ? "hidden xl:block" : ""}`}>
        <header>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Conversaciones</h2>
          <p className="text-sm text-slate-500 mt-1">
            Las personas que le escribieron al número de WhatsApp de la marca. Se contesta con texto libre hasta 24 h después de su último mensaje.
          </p>
        </header>
        <FiltrosConversaciones filtro={filtro} campana={campana} campanas={pagina?.campanas ?? []} texto={texto}
          onFiltro={(f: FiltroConversaciones) => setFiltroUrl("filtro", f === "por_contestar" ? "" : f)}
          onCampana={(c) => setFiltroUrl("campana", c)} onTexto={setTexto} />
      </div>

      <div className="xl:grid xl:min-h-0 xl:flex-1 xl:grid-cols-12 xl:grid-rows-[minmax(0,1fr)] xl:gap-4">
        <div data-testid="columna-lista"
          className={`xl:col-span-5 space-y-4 xl:relative xl:overflow-y-auto ${hayId ? "hidden xl:block" : ""}`}>
          {mostrarBotonResolver && (
            <button type="button" onClick={() => setResolviendo(true)}
              className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-sm font-semibold text-amber-800 hover:bg-amber-100">
              Marcar resueltas las que tienen la ventana cerrada ({num(vencidas)})
            </button>
          )}
          {resolviendo && <TarjetaResolverVencidas vencidas={vencidas} onCerrar={() => setResolviendo(false)} />}

          <ListaConversaciones pagina={filasDeOtrosFiltros ? null : pagina} cargando={cargando} error={error}
            onReintentar={() => { setCargando(true); void cargar(); }}
            detenido={poll.detenido} onReintentarPolling={poll.reintentar}
            seleccionadaId={seleccionadaId} ahora={new Date(Date.now() + desfase)} />
        </div>
        <div data-testid="columna-hilo" className={`xl:col-span-7 xl:flex xl:min-h-0 xl:flex-col ${hayId ? "" : "hidden"}`}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
